import { useCallback, useEffect, useState } from 'react';
import { Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';

const SECRET_FIELDS = ['git_token', 'test_password', 'prod_db_dsn', 'extra_env', 'webhook_secret'];

// Per-project settings of the Claude agent (owner only). Secrets are write-only:
// the API returns just whether each one is set.
export default function ProjectAgentSettingsPanel({ project, users = [], onProjectChange }) {
  const t = useT();
  const { user } = useAuth();
  const isOwner = user && project.owner_id === user.id;
  const [form, setForm] = useState(null);
  const [secretsSet, setSecretsSet] = useState({});
  const [secrets, setSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isOwner) return;
    try {
      const { data } = await api.get(`/projects/${project.id}/agent-settings`);
      const { secrets_set: set, ...settings } = data;
      setForm(settings);
      setSecretsSet(set || {});
      setSecrets({});
    } catch (err) {
      toast.error(extractErrorMessage(err, t('agent.settings.loadFailed')));
    }
  }, [isOwner, project.id, t]);

  useEffect(() => { load(); }, [load]);

  if (!isOwner || !form) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const aiUserIds = form.ai_user_ids || [];
  const toggleAiUser = (id, on) => setForm({
    ...form,
    ai_user_ids: on ? [...aiUserIds, id] : aiUserIds.filter((x) => x !== id),
  });
  const otherMembers = (project.member_ids || [])
    .filter((id) => id !== project.owner_id)
    .map((id) => users.find((u) => u.id === id) || { id });
  const setNum = (key) => (e) => setForm({ ...form, [key]: Number(e.target.value) || 0 });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send secrets the owner touched: typed = replace, cleared = ''.
      const payloadSecrets = Object.fromEntries(
        SECRET_FIELDS.filter((k) => secrets[k] !== undefined).map((k) => [k, secrets[k]]),
      );
      const { data } = await api.put(`/projects/${project.id}/agent-settings`, { ...form, secrets: payloadSecrets });
      const { secrets_set: setFlags, ...settings } = data;
      setForm(settings);
      setSecretsSet(setFlags || {});
      setSecrets({});
      onProjectChange && onProjectChange({ ...project, agent_enabled: settings.enabled, ai_user_ids: settings.ai_user_ids });
      toast.success(t('agent.settings.saved'));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.saveFailedToast')));
    } finally {
      setSaving(false);
    }
  };

  const label = 'text-xs uppercase tracking-wider font-semibold';
  const section = 'text-[10px] uppercase tracking-wider font-semibold text-zinc-500 pt-2';

  const secretInput = (name, labelKey, { multiline = false, hintKey } = {}) => {
    const isSet = secretsSet[name];
    const cleared = secrets[name] === '';
    const placeholder = cleared ? t('agent.settings.secretEmpty') : isSet ? t('agent.settings.secretSet') : t('agent.settings.secretEmpty');
    const Field = multiline ? Textarea : Input;
    return (
      <div>
        <div className="flex items-center justify-between">
          <Label className={label}>{t(labelKey)}</Label>
          {isSet && !cleared && (
            <button
              type="button"
              className="text-[10px] text-red-600 hover:underline"
              onClick={() => setSecrets({ ...secrets, [name]: '' })}
            >
              {t('agent.settings.clear')}
            </button>
          )}
        </div>
        {hintKey && <p className="text-[11px] text-zinc-500 mt-0.5">{t(hintKey)}</p>}
        <Field
          data-testid={`agent-secret-${name}`}
          type={multiline ? undefined : 'password'}
          autoComplete="new-password"
          rows={multiline ? 3 : undefined}
          value={secrets[name] ?? ''}
          placeholder={placeholder}
          onChange={(e) => setSecrets({ ...secrets, [name]: e.target.value })}
          className="rounded-sm mt-1 font-mono text-xs"
        />
      </div>
    );
  };

  return (
    <form onSubmit={save} className="space-y-3" data-testid="agent-settings-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1">
            <Robot size={12} weight="bold" /> {t('agent.settings.title')}
          </p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{t('agent.settings.hint')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-xs">{t('agent.settings.enabled')}</Label>
          <Switch
            data-testid="agent-settings-enabled"
            checked={!!form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
          />
        </div>
      </div>

      <div>
        <Label className={label}>{t('agent.settings.aiUsers')}</Label>
        <p className="text-[11px] text-zinc-500 mt-0.5">{t('agent.settings.aiUsersHint')}</p>
        {otherMembers.length === 0 ? (
          <p className="text-[11px] text-zinc-400 mt-1">{t('agent.settings.aiUsersEmpty')}</p>
        ) : (
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1" data-testid="agent-settings-ai-users">
            {otherMembers.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer min-w-0">
                <Checkbox
                  data-testid={`agent-ai-user-${m.id}`}
                  checked={aiUserIds.includes(m.id)}
                  onCheckedChange={(v) => toggleAiUser(m.id, v === true)}
                />
                <span className="truncate">{m.name || m.email || m.id}</span>
                {m.name && m.email && <span className="truncate text-zinc-400">{m.email}</span>}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <Label className={label}>{t('agent.settings.model')}</Label>
          <Input value={form.model} onChange={set('model')} placeholder={t('agent.settings.modelPlaceholder')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.maxTurns')}</Label>
          <Input type="number" min={1} value={form.max_turns} onChange={setNum('max_turns')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.timeout')}</Label>
          <Input type="number" min={5} value={form.run_timeout_min} onChange={setNum('run_timeout_min')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.testLoops')}</Label>
          <Input type="number" min={1} value={form.max_test_loops} onChange={setNum('max_test_loops')} className="rounded-sm mt-1 text-xs" />
        </div>
      </div>

      <div>
        <Label className={label}>{t('agent.settings.instructions')}</Label>
        <Textarea rows={4} value={form.instructions} onChange={set('instructions')} placeholder={t('agent.settings.instructionsPlaceholder')} className="rounded-sm mt-1 text-xs" />
      </div>

      <p className={section}>{t('agent.settings.stack')}</p>
      <p className="text-[11px] text-zinc-500 -mt-2">{t('agent.settings.stackHint')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={label}>{t('agent.settings.stackUp')}</Label>
          <Textarea rows={6} value={form.stack_up} onChange={set('stack_up')} className="rounded-sm mt-1 font-mono text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.stackDown')}</Label>
          <Textarea rows={6} value={form.stack_down} onChange={set('stack_down')} className="rounded-sm mt-1 font-mono text-xs" />
        </div>
      </div>

      <p className={section}>{t('agent.settings.login')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className={label}>{t('agent.settings.keycloakUrl')}</Label>
          <Input value={form.keycloak_url} onChange={set('keycloak_url')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.realm')}</Label>
          <Input value={form.keycloak_realm} onChange={set('keycloak_realm')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.clientId')}</Label>
          <Input value={form.keycloak_client_id} onChange={set('keycloak_client_id')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div>
          <Label className={label}>{t('agent.settings.username')}</Label>
          <Input value={form.test_username} onChange={set('test_username')} className="rounded-sm mt-1 text-xs" />
        </div>
        <div className="sm:col-span-2">{secretInput('test_password', 'agent.settings.password')}</div>
      </div>

      <p className={section}>{t('agent.settings.prod')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {secretInput('prod_db_dsn', 'agent.settings.prodDsn')}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={label}>{t('agent.settings.kubeContext')}</Label>
            <Input value={form.prod_kube_context} onChange={set('prod_kube_context')} className="rounded-sm mt-1 text-xs" />
          </div>
          <div>
            <Label className={label}>{t('agent.settings.loki')}</Label>
            <Input value={form.loki_url} onChange={set('loki_url')} className="rounded-sm mt-1 text-xs" />
          </div>
        </div>
      </div>

      <p className={section}>{t('agent.settings.git')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={label}>{t('agent.settings.gitUsername')}</Label>
          <Input value={form.git_username} onChange={set('git_username')} className="rounded-sm mt-1 text-xs" />
        </div>
        {secretInput('git_token', 'agent.settings.gitToken', { hintKey: 'agent.settings.gitTokenHint' })}
      </div>

      {secretInput('extra_env', 'agent.settings.extraEnv', { multiline: true })}

      <p className={section}>{t('agent.settings.inbox')}</p>
      <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
        <Checkbox
          data-testid="agent-settings-auto-start"
          checked={!!form.auto_start}
          onCheckedChange={(v) => setForm({ ...form, auto_start: v === true })}
        />
        {t('agent.settings.autoStart')}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={label}>{t('agent.settings.webhookUrl')}</Label>
          <p className="text-[11px] text-zinc-500 mt-0.5">{t('agent.settings.webhookUrlHint')}</p>
          <Input value={form.webhook_url || ''} onChange={set('webhook_url')} placeholder="https://…" className="rounded-sm mt-1 text-xs" />
        </div>
        {secretInput('webhook_secret', 'agent.settings.webhookSecret', { hintKey: 'agent.settings.webhookSecretHint' })}
      </div>

      <div className="flex justify-end">
        <Button type="submit" data-testid="agent-settings-save" disabled={saving} className="rounded-sm bg-black text-white hover:bg-zinc-800">
          {saving ? t('settings.saving') : t('agent.settings.save')}
        </Button>
      </div>
    </form>
  );
}
